import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, BookOpen, Headphones, Calendar, User, LogOut, Menu, X, Settings, Feather } from 'lucide-react';
import api from '../services/api';
import ReferralButton from './ReferralButton';
import NotificationBell from './NotificationBell';
import GlobalSearch from './GlobalSearch';
import { OfflineIndicator } from './OfflineContentManager';

const UserLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasRutaCritica, setHasRutaCritica] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.is_admin;

  // Check if user has Ruta Crítica enrollment
  useEffect(() => {
    const checkEnrollment = async () => {
      try {
        const res = await api.get('/enrollment/check/ruta-critica');
        setHasRutaCritica(res.data?.enrolled === true);
      } catch (err) {
        // If error (401 or 404), assume no enrollment
        setHasRutaCritica(false);
      }
    };
    
    if (user) {
      checkEnrollment();
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Build nav items dynamically based on enrollment
  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Inicio' },
    { path: '/dashboard/cursos', icon: BookOpen, label: 'Cursos' },
    { path: '/dashboard/videos', icon: Headphones, label: 'Media' },
    { path: '/blog', icon: Feather, label: 'Blog' },
    // Only show Agendar if user has Ruta Crítica enrollment or is admin
    ...(hasRutaCritica || isAdmin ? [{ path: '/dashboard/booking', icon: Calendar, label: 'Agendar' }] : []),
    { path: '/dashboard/profile', icon: User, label: 'Perfil' },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-black">
      <OfflineIndicator />
      {/* Mobile Header */}
      <header className="lg:hidden bg-zinc-900 border-b border-zinc-800 px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
            <i className="fa-solid fa-fire text-white text-sm"></i>
          </div>
          <span 
            className="text-white font-black tracking-tight"
            style={{ fontFamily: 'Impact, Haettenschweiler, Arial Narrow Bold, sans-serif' }}
          >
            FECONCIENCIA
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <GlobalSearch />
          <NotificationBell />
          <ReferralButton />
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/80 z-40" onClick={() => setMobileMenuOpen(false)} />
          <nav className="lg:hidden fixed top-0 right-0 h-full w-72 bg-zinc-900 z-50 p-4">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-zinc-800">
              <span className="text-white font-bold">Menú</span>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-zinc-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-orange-500 text-white'
                      : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              ))}
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-orange-400 hover:bg-orange-500/20 border border-orange-500/30 mt-4"
                >
                  <Settings className="w-5 h-5" />
                  <span>Panel Admin</span>
                </Link>
              )}
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center text-white">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{user?.name}</p>
                  <p className="text-zinc-500 text-xs">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-white transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Cerrar sesión
              </button>
            </div>
          </nav>
        </>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-64 bg-zinc-900 border-r border-zinc-800 flex-col">
        <div className="p-4 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <i className="fa-solid fa-fire text-white"></i>
            </div>
            <span 
              className="text-white font-black text-lg tracking-tight"
              style={{ fontFamily: 'Impact, Haettenschweiler, Arial Narrow Bold, sans-serif' }}
            >
              FECONCIENCIA
            </span>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {/* Global Search */}
          <div className="mb-3">
            <GlobalSearch />
          </div>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive(item.path)
                  ? 'bg-orange-500 text-white'
                  : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Link>
          ))}
          {isAdmin && (
            <Link
              to="/admin"
              className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-orange-400 hover:bg-orange-500/20 border border-orange-500/30 mt-4"
              data-testid="admin-panel-link"
            >
              <Settings className="w-5 h-5" />
              <span>Panel Admin</span>
            </Link>
          )}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          {/* User Profile with Referral Button */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center text-white">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-zinc-500 text-xs truncate">{user?.email}</p>
            </div>
            <NotificationBell />
            <ReferralButton />
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default UserLayout;
